// The inbox's answers while offline (docs/design/social-plan.md 5): an accept, decline, cancel or
// reminder tapped without a connection is kept on a proef.-prefixed localStorage key under the
// signed-in person's id, and replayed in order once the device is back online. Every action is
// idempotent at the edge, and an answer the edge refuses because it was settled elsewhere (gone,
// already done) counts as done, so a replay never repeats an effect. Signing out forgets the queue.
import { useSyncExternalStore } from "react";
import { EdgeApiError } from "../edgeApi";
import { acceptFriendRequest, cancelFriendRequest, declineFriendRequest } from "./friendsApi";
import { acceptGroupJoinRequest, declineGroupJoinRequest } from "./groupsApi";
import {
  acceptGroupInvite,
  cancelGroupInvite,
  declineGroupInvite,
  declineSessionInvite,
  markInboxRead,
  remindGroupInvite,
} from "./inboxApi";
import { revokeInvite } from "./invitesApi";
import { dropDraft, sendDraft } from "../tasting/draftApi";
import { rsvpSession } from "./sessionsApi";
import type { Rsvp } from "./tastingSession";

export type SocialAction =
  | {
      readonly kind: "friend_accept" | "friend_decline" | "friend_cancel";
      readonly friendshipId: string;
    }
  | {
      readonly kind:
        | "group_invite_accept"
        | "group_invite_decline"
        | "group_invite_cancel"
        | "group_invite_remind"
        | "join_accept"
        | "join_decline";
      readonly groupId: string;
      readonly userId: string;
    }
  | { readonly kind: "session_decline"; readonly sessionId: string }
  // The tasting itself is read when the action runs, so however many step changes piled up while
  // offline, one write carries the latest copy and the queue never holds content.
  | { readonly kind: "draft_put" | "draft_delete"; readonly runId: string }
  | { readonly kind: "session_rsvp"; readonly sessionId: string; readonly rsvp: Rsvp }
  | { readonly kind: "link_revoke"; readonly inviteId: string }
  | { readonly kind: "read"; readonly before: string };

export interface QueuedAction {
  readonly id: string;
  readonly userId: string;
  readonly action: SocialAction;
}

const STORAGE_KEY = "proef.socialOutbox";
/** Far more than anyone answers offline; the cap only bounds a broken loop. */
const MAX_ITEMS = 100;
/** The edge's way of saying an answer was already settled: by another device, or by the other person. */
const SETTLED = new Set([404, 409, 410]);

const ACTION_KINDS = new Set<string>([
  "friend_accept",
  "friend_decline",
  "friend_cancel",
  "group_invite_accept",
  "group_invite_decline",
  "group_invite_cancel",
  "group_invite_remind",
  "join_accept",
  "join_decline",
  "session_decline",
  "draft_put",
  "draft_delete",
  "session_rsvp",
  "link_revoke",
  "read",
]);

function isQueued(value: unknown): value is QueuedAction {
  if (typeof value !== "object" || value === null) return false;
  const action: unknown = Reflect.get(value, "action");
  return (
    typeof Reflect.get(value, "id") === "string" &&
    typeof Reflect.get(value, "userId") === "string" &&
    typeof action === "object" &&
    action !== null &&
    ACTION_KINDS.has(String(Reflect.get(action, "kind")))
  );
}

function read(): QueuedAction[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isQueued) : [];
  } catch {
    return [];
  }
}

let queue: readonly QueuedAction[] = typeof window === "undefined" ? [] : read();
const listeners = new Set<() => void>();

function commit(next: readonly QueuedAction[]): void {
  queue = next;
  try {
    if (next.length === 0) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked: the queue lasts this session.
  }
  listeners.forEach((listener) => {
    listener();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** What is waiting for this person, oldest first. */
export function queuedActions(userId: string): readonly QueuedAction[] {
  return queue.filter((entry) => entry.userId === userId);
}

const EMPTY: readonly QueuedAction[] = [];

export function useQueuedActions(userId: string | undefined): readonly QueuedAction[] {
  return useSyncExternalStore(
    subscribe,
    () => (userId === undefined ? EMPTY : queue),
    () => EMPTY,
  ).filter((entry) => entry.userId === userId);
}

async function run(action: SocialAction, userId: string): Promise<unknown> {
  switch (action.kind) {
    case "draft_put":
      return sendDraft(userId, action.runId);
    case "draft_delete":
      return dropDraft(action.runId);
    case "friend_accept":
      return acceptFriendRequest(action.friendshipId);
    case "friend_decline":
      return declineFriendRequest(action.friendshipId);
    case "friend_cancel":
      return cancelFriendRequest(action.friendshipId);
    case "group_invite_accept":
      return acceptGroupInvite(action.groupId, action.userId);
    case "group_invite_decline":
      return declineGroupInvite(action.groupId, action.userId);
    case "group_invite_cancel":
      return cancelGroupInvite(action.groupId, action.userId);
    case "group_invite_remind":
      return remindGroupInvite(action.groupId, action.userId);
    case "join_accept":
      return acceptGroupJoinRequest(action.groupId, action.userId);
    case "join_decline":
      return declineGroupJoinRequest(action.groupId, action.userId);
    case "session_decline":
      return declineSessionInvite(action.sessionId);
    case "session_rsvp":
      return rsvpSession(action.sessionId, action.rsvp);
    case "link_revoke":
      return revokeInvite(action.inviteId);
    default:
      return markInboxRead(action.before);
  }
}

/** No answer at all, or the edge down or switched off: worth trying again later. */
function unreachable(error: unknown): boolean {
  return error instanceof TypeError || (error instanceof EdgeApiError && error.status >= 500);
}

/**
 * Sends an answer now, or keeps it for later when there is no connection. "done" covers an answer
 * the edge says was already settled; any other refusal is thrown for the screen to show.
 */
export async function perform(userId: string, action: SocialAction): Promise<"done" | "queued"> {
  const keep = (): "queued" => {
    const draft = "runId" in action;
    // One waiting write per tasting is enough, and a delete makes the writes before it pointless.
    const rest = draft
      ? queue.filter(
          (entry) =>
            !(
              entry.userId === userId &&
              "runId" in entry.action &&
              entry.action.runId === action.runId &&
              (entry.action.kind === action.kind || action.kind === "draft_delete")
            ),
        )
      : queue;
    commit([...rest, { id: crypto.randomUUID(), userId, action }].slice(-MAX_ITEMS));
    return "queued";
  };
  if (typeof navigator !== "undefined" && !navigator.onLine) return keep();
  try {
    await run(action, userId);
    return "done";
  } catch (error) {
    if (unreachable(error)) return keep();
    if (error instanceof EdgeApiError && SETTLED.has(error.status)) return "done";
    throw error;
  }
}

let flushing: Promise<void> | undefined;

/** Replays this person's queue in order, stopping at the first one that still cannot get through. */
export function flushSocialOutbox(userId: string): Promise<void> {
  flushing ??= (async () => {
    try {
      for (const entry of queuedActions(userId)) {
        try {
          await run(entry.action, userId);
        } catch (error) {
          if (unreachable(error)) return;
          // Any other answer is final: replaying it would only be refused again.
        }
        commit(queue.filter((queued) => queued.id !== entry.id));
      }
    } finally {
      flushing = undefined;
    }
  })();
  return flushing;
}

/** Signing out takes the queue with it: the next person on this device must not send it. */
export function clearSocialOutbox(): void {
  commit([]);
}

/** Tests start from an empty queue read fresh from storage. */
export function resetSocialOutbox(): void {
  queue = read();
}
